/**
 * INUIKII – Storewide sale (display-only)
 *
 * Drives the crossed-out / reduced price that the client-side Meilisearch
 * surfaces show for products in the configured sale collections:
 *   - collection grid  (aico-collection-meilisearch.js)
 *   - search results   (search-meilisearch.js)
 *   - predictive search (predictive-search.js)
 *
 * This is DISPLAY ONLY. The price is actually reduced in cart/checkout by a real
 * Shopify automatic discount ("Amount off products", set to not combine) that the
 * merchant configures for the SAME collections at the SAME percent. The percent
 * below must equal that discount so the browsing price matches checkout.
 *
 * Reads window.StorewideSaleConfig = { enabled, percent, currency, collectionIds }
 * (injected by sections/aico-header.liquid) and exposes window.StorewideSale.
 *
 * Prices handled here are in MAJOR units (e.g. 139.00), matching the Meilisearch
 * hit prices. The Rappen rounding is intentionally identical to
 * snippets/storewide-sale-price.liquid (which works in cents) so Liquid and JS
 * surfaces always show the same figure.
 *
 * Note: if a customer/B2B pricing engine is ever added, it should take priority
 * over this display sale — apply this only to standard retail prices.
 */
(function (root) {
  "use strict";

  /**
   * @param {{enabled?:boolean, percent?:number, currency?:string, collectionIds?:Array<string|number>}|null} cfg
   */
  function makeEngine(cfg) {
    var percent = cfg ? Number(cfg.percent) || 0 : 0;
    var currency = (cfg && cfg.currency) || "CHF";
    var ids =
      cfg && Array.isArray(cfg.collectionIds)
        ? cfg.collectionIds.map(String)
        : [];
    var enabled = !!(cfg && cfg.enabled) && percent > 0 && ids.length > 0;

    /**
     * True when the hit belongs to at least one configured sale collection.
     * Matches against hit.collections[].id (compared as strings), exactly like
     * the existing hide-colour logic.
     * @param {any} hit
     * @returns {boolean}
     */
    function isEligible(hit) {
      if (!enabled || !hit || !Array.isArray(hit.collections)) return false;
      return hit.collections.some(function (c) {
        return c && ids.indexOf(String(c.id)) !== -1;
      });
    }

    /**
     * price × (1 − percent/100), rounded to the nearest 0.05 for CHF (Rappen)
     * or the nearest cent otherwise. Input/output are in major units.
     * @param {number} price
     * @returns {number}
     */
    function salePrice(price) {
      var raw = ((Number(price) || 0) * (100 - percent)) / 100;
      if (currency === "CHF") return Math.round(raw * 20) / 20; // nearest 0.05
      return Math.round(raw * 100) / 100; // nearest cent
    }

    /**
     * Returns the price + compare-at to display for a hit. When the hit is
     * sale-eligible the price is discounted and the original becomes the
     * crossed-out compare-at; otherwise the inputs are returned unchanged.
     * @param {number} price - current price (major units)
     * @param {number} cmp - current compare-at (major units, 0 if none)
     * @param {any} hit
     * @returns {{price:number, cmp:number}}
     */
    function apply(price, cmp, hit) {
      if (!isEligible(hit)) return { price: price, cmp: cmp };
      var original = Number(price) || 0;
      return { price: salePrice(original), cmp: original };
    }

    return {
      enabled: enabled,
      percent: percent,
      currency: currency,
      collectionIds: ids,
      isEligible: isEligible,
      salePrice: salePrice,
      apply: apply,
    };
  }

  var cfg =
    (root && root.StorewideSaleConfig) ||
    (typeof window !== "undefined" && window.StorewideSaleConfig) ||
    null;
  var engine = makeEngine(cfg);

  if (typeof window !== "undefined") window.StorewideSale = engine;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { makeEngine: makeEngine, StorewideSale: engine };
  }
})(typeof globalThis !== "undefined" ? globalThis : this);
